import React, { FC, useCallback } from 'react';

import dynamic from 'next/dynamic';

import { useBreakpoint } from '@contexts/breakpoints';
import PriceDetailsDrawer from '@components/ui/PriceDetailsDrawer/PriceDetailsDrawer';
import { Currencies } from '@constants/constants';
import { FlexibleDiv, Row } from '@components/formStyles';
import {
  ItemBoldText,
  ItemText,
  WarningMessage,
  ItemsCol,
} from '@components/ui/PriceDetailsDrawer/Item';
import CustomIcon from '@components/utils/CustomIcon';
import { HotelIcon } from '@assets/icons';

const DynamicListItemCircle = dynamic(
  () =>
    import('@components/ui/PriceDetailsDrawer/Item').then(
      (mod: any) => mod['ListItemCircle']
    ) as Promise<React.FC<any>>
);
export interface PriceDetailsDrawerItem {
  id: string;
  name?: string;
  price: number;
  milePrice: number;
  tabNumber: number;
}

export interface PriceDetailsProps {
  items: PriceDetailsDrawerItem[];
  onNavigate: () => void;
  onRemove: (id: string) => void;
  currency?: string;
  warningMessage?: string;
  toggleConfirmModal: () => void;
  itemsInBasket?: number;
}

const PriceDetails: FC<PriceDetailsProps> = ({
  onNavigate,
  onRemove,
  items,
  currency = Currencies.TL,
  warningMessage,
  toggleConfirmModal,
  itemsInBasket,
}) => {
  const { md: isDesktop } = useBreakpoint();
  const totalPrice = useCallback(
    (): number =>
      items.length &&
      items.map((item) => item.price).reduce((a: number, b: number) => a + b),
    [items]
  );

  const totalMile = useCallback(
    (): number =>
      items.length &&
      items
        .map((item) => item.milePrice)
        .reduce((a: number, b: number) => a + b),
    [items]
  );

  const handleNavigateTravelPlanning = () => {
    if (itemsInBasket === 0) {
      onNavigate();
    } else {
      toggleConfirmModal();
    }
  };

  return (
    <PriceDetailsDrawer
      closable={isDesktop ? items.length > 2 : true}
      totalPrice={totalPrice()}
      totalMilePrice={totalMile()}
      onNavigate={handleNavigateTravelPlanning}
      disabled={!!warningMessage}
    >
      <Row>
        <CustomIcon
          fontSize={24}
          icon={HotelIcon}
          paletteColor='primaryBrand'
        />
        <FlexibleDiv right={10} />
        <ItemsCol>
          {items
            .sort((a, b) => (a.tabNumber > b.tabNumber ? 1 : -1))
            .map((item) => (
              <PriceDetailsDrawer.Item
                key={item.id}
                content={
                  <Row alignItems='center'>
                    <ItemBoldText>{item.tabNumber}.Oda</ItemBoldText>
                    {isDesktop ? (
                      <DynamicListItemCircle
                        currency={currency}
                        onRemove={() => onRemove(item.id)}
                        price={item.price}
                        showPrice={!!isDesktop}
                      />
                    ) : null}
                    <ItemText>{item.name}</ItemText>
                  </Row>
                }
                currency={currency}
                onRemove={() => onRemove(item.id)}
                price={item.price}
                showPrice={!!isDesktop}
              />
            ))}
        </ItemsCol>
      </Row>

      {warningMessage ? (
        <WarningMessage>{warningMessage}</WarningMessage>
      ) : null}
    </PriceDetailsDrawer>
  );
};

export default PriceDetails;
